import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, Group, Stack, Text} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useCallback, useEffect} from 'react';
import isEqual from 'react-fast-compare';

import * as OrgActions from 'actions/OrganizationActions';
import * as UserActions from 'actions/UserActions';
import * as analytics from 'analytics/Analytics';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {User} from 'constants/UserConstants';
import EntityName from 'orgs/EntityName';
import {Organization} from 'orgs/OrganizationConstants';
import EntityStore from 'stores/EntityStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

import HomepageBanner from './HomepageBanner';
import {HOMEPAGE_BANNER_TEST_COMPONENT} from './HomepageConstants';

const TEST_COMPONENT = HOMEPAGE_BANNER_TEST_COMPONENT.ORGANIZATION_INVITE;

const useStyles = createStyles((theme) => ({
  text: {
    fontSize: theme.fontSizes.md,
  },
  title: {
    fontWeight: theme.other.fontWeights.semibold,
  },
}));

interface OrganizationInvitesData {
  currentUserInboundRequests: NonNullable<EntityStoreUser['currentUserInboundRequests']>;
  userOrgsInboundRequests: NonNullable<EntityStoreUser['userOrgsInboundRequests']>;
  pendingOrgInvites: number;
}

function useOrganizationInvites(): OrganizationInvitesData | null {
  useEffect(() => {
    UserActions.loadOrgRequests();
  }, []);

  const inviteData = useStoreSelector(
    EntityStore,
    useCallback((store) => {
      const currentUser = store.currentUser() as EntityStoreUser | null;
      if (currentUser == null) {
        return null;
      }
      return {
        currentUserInboundRequests: currentUser.currentUserInboundRequests ?? [],
        userOrgsInboundRequests: currentUser.userOrgsInboundRequests ?? [],
        pendingOrgInvites: currentUser.pendingOrgInvites ?? 0,
      };
    }, []),
    isEqual
  );

  if (
    inviteData == null ||
    (inviteData.currentUserInboundRequests.length === 0 &&
      inviteData.userOrgsInboundRequests.length === 0 &&
      inviteData.pendingOrgInvites === 0)
  ) {
    return null;
  }

  return inviteData;
}

const RECEIVED_INVITE_REGION_LABEL = 'Organization invite';
const JOIN_REQUEST_REGION_LABEL = 'Join request';

interface InviteActionButtonsProps {
  onAccept: () => void;
  onReject: () => void;
}

function InviteActionButtons({onAccept, onReject}: InviteActionButtonsProps): JSX.Element {
  return (
    <Group spacing="xs" noWrap={true}>
      <Button
        // eslint-disable-next-line no-restricted-syntax -- white secondary action on blue banner background
        variant="default"
        radius="md"
        size="md"
        onClick={onReject}
      >
        Reject
      </Button>
      <Button variant="primary" radius="md" size="md" onClick={onAccept}>
        Accept
      </Button>
    </Group>
  );
}

interface ReceivedInviteBannerProps {
  organization: Organization;
  inviter: User;
}

function ReceivedInviteBanner({organization, inviter}: ReceivedInviteBannerProps): JSX.Element {
  const {classes} = useStyles();
  const messageId = useId();

  const handleAccept = (): void => {
    UserActions.acceptOutboundRequest(organization.id);
  };

  const handleReject = (): void => {
    UserActions.rejectOutboundRequest(organization.id);
  };

  useEffect(() => {
    analytics.track('organizations', 'viewed_organization_invite', {
      user_id: EntityStore.currentUser()?.id,
      organization_id: organization.id,
    });
  }, [organization.id]);

  return (
    <HomepageBanner
      color="blue"
      role="region"
      aria-describedby={messageId}
      aria-label={RECEIVED_INVITE_REGION_LABEL}
      dataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'receivedInvite',
        key: `${organization.id}-${inviter.id}`,
      }}
      rightSection={<InviteActionButtons onAccept={handleAccept} onReject={handleReject} />}
    >
      <Box className={classes.text}>
        <Text id={messageId}>
          <EntityName entity={inviter} /> invited you to join <EntityName entity={organization} />.
        </Text>
      </Box>
    </HomepageBanner>
  );
}

interface JoinRequestBannerProps {
  organization: Organization;
  user: User;
}

function JoinRequestBanner({organization, user}: JoinRequestBannerProps): JSX.Element {
  const {classes} = useStyles();
  const messageId = useId();

  const handleAccept = (): void => {
    OrgActions.acceptInboundRequest(organization.id, user.id);
  };

  const handleReject = (): void => {
    OrgActions.rejectInboundRequest(organization.id, user.id);
  };

  useEffect(() => {
    analytics.track('organizations', 'viewed_join_organization_request', {
      user_id: EntityStore.currentUser()?.id,
      organization_id: organization.id,
      requestor_id: user.id,
    });
  }, [organization.id, user.id]);

  return (
    <HomepageBanner
      color="blue"
      role="region"
      aria-describedby={messageId}
      aria-label={JOIN_REQUEST_REGION_LABEL}
      dataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'joinRequest',
        key: `${organization.id}-${user.id}`,
      }}
      rightSection={<InviteActionButtons onAccept={handleAccept} onReject={handleReject} />}
    >
      <Box className={classes.text}>
        <Text id={messageId}>
          <EntityName entity={user} /> has requested to join <EntityName entity={organization} />.
        </Text>
      </Box>
    </HomepageBanner>
  );
}

function PendingEmailInviteBanner({pendingOrgInvites}: {pendingOrgInvites: number}): JSX.Element {
  const {classes} = useStyles();
  const titleId = useId();
  const hasMultipleInvites = pendingOrgInvites > 1;
  const title = hasMultipleInvites ? 'You have organization invites' : 'You have an organization invite';

  return (
    <HomepageBanner color="blue" role="region" aria-labelledby={titleId}>
      <Box className={classes.text}>
        <Text className={classes.title} id={titleId}>
          {title}
        </Text>
        <Text>
          {hasMultipleInvites
            ? 'Accept the invites in your email inbox or verify your email address to see the invites here.'
            : 'Accept the invite in your email inbox or verify your email address to see the invite here.'}
        </Text>
      </Box>
    </HomepageBanner>
  );
}

export default function OrganizationInviteBanner(): JSX.Element | null {
  const inviteData = useOrganizationInvites();

  return inviteData == null ? null : (
    <Stack spacing="sm" mb="sm" {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
      {inviteData.currentUserInboundRequests.map(({organization, inviter}) => (
        <ReceivedInviteBanner
          key={`${organization.id}-${inviter.id}`}
          organization={organization}
          inviter={inviter}
        />
      ))}
      {inviteData.userOrgsInboundRequests.map(({organization, user}) => (
        <JoinRequestBanner key={`${organization.id}-${user.id}`} organization={organization} user={user} />
      ))}
      {inviteData.pendingOrgInvites > 0 && (
        <PendingEmailInviteBanner pendingOrgInvites={inviteData.pendingOrgInvites} />
      )}
    </Stack>
  );
}
